<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

import type { Editor } from "@tiptap/vue-3";
import type { TypoLevel } from "./interfaces";

import TcIconButton from "@/Components/Extend/TcIconButton.vue";
import TcSelect, { type Option } from "@/Components/UI/TcSelect.vue";
import NumberField from "./components/NumberField.vue";
import ColorPicker, { BASE_COLOR } from "./components/ColorPicker.vue";
import LinkField, { type Link } from "./components/LinkField.vue";

import { hasValue } from "@/Utils/Objects";

import { TEXT_TYPE_OPTIONS, ZOOM_OPTIONS, FONT_OPTIONS } from "./assets";

import { isValidString } from "@/Utils/String";

export interface ToolbarProps {
  editor: Editor;
  zoom?: number;
}

enum ALIGN {
  LEFT = "left",
  CENTER = "center",
  RIGHT = "right",
  JUSTIFY = "justify",
}

enum LIST_TYPE {
  BULLET = "bulletList",
  ORDERED = "orderedList",
  TASK = "taskList",
}

const { t } = useI18n();

const props = withDefaults(defineProps<ToolbarProps>(), { zoom: 1 });

const emit = defineEmits<{
  (e: "update:zoom", value: number): void;
}>();

const currentZoom = ref<Option<number>>(ZOOM_OPTIONS[3]!);
const currentFontFamily = ref<Option<string>>();
const currentFontSize = ref<number>(16);
const currentColor = ref<string>(BASE_COLOR);
const currentAlign = ref<ALIGN | undefined>(undefined);
const currentListType = ref<LIST_TYPE | undefined>(undefined);
const currentLink = ref<boolean>(false);

const textOptions = computed((): Option<number>[] =>
  TEXT_TYPE_OPTIONS.map((o) => ({ ...o, label: t(o.label) }))
);
const fontOptions = computed(() => FONT_OPTIONS.map((o) => ({ ...o, label: t(o.label) })));

const getEditor = () => props.editor;
const getChain = () => getEditor().chain();
const getFocus = () => getChain().focus();
const isActive = (prop: string | object) => getEditor().isActive(prop);
const isActiveHeading = (level: number) => getEditor().isActive("heading", { level });
const getAttributes = (prop: string) => getEditor().getAttributes(prop);
const selectedTextTypeOption = (): Option<number> | undefined => {
  if (isActive("paragraph")) return TEXT_TYPE_OPTIONS[0];

  let level = 1;
  let cent = false;

  while (!cent && level < TEXT_TYPE_OPTIONS.length) {
    if (isActiveHeading(level)) {
      cent = true;
    }

    level++;
  }

  if (cent) {
    return TEXT_TYPE_OPTIONS[level - 1];
  }

  return undefined;
};

const handleSelectTypo = (option?: Option<number>) => {
  if (!hasValue(option)) return;

  if (option.value === 0) {
    getFocus().setParagraph().run();

    return;
  }

  getFocus()
    .setHeading({ level: option.value as TypoLevel })
    .run();
};

const handleSelectFont = (option?: Option<string>) => {
  if (!hasValue(option)) return;

  getFocus().setFontFamily(option.value).run();
};

const handleSetFontSize = (size: number) => {
  if (!hasValue(size)) return;

  currentFontSize.value = size;

  getFocus().setFontSize(`${size}px`).run();
};

const updateZoom = (newValue: number) => {
  if (currentZoom.value.value !== newValue) {
    const zoom = ZOOM_OPTIONS.find((o) => o.value === newValue);

    if (hasValue(zoom)) {
      currentZoom.value = zoom;
    }
  }
};

const handleChangeZoom = (op?: Option<number>) => {
  if (!hasValue(op)) return;

  if (props.zoom !== op.value) {
    emit("update:zoom", op!.value);
  }
};

const handleBold = () => {
  getFocus().toggleBold().run();
};

const handleItalic = () => {
  getFocus().toggleItalic().run();
};

const handleUnderline = () => {
  getFocus().toggleUnderline().run();
};

const handleStrike = () => {
  getFocus().toggleStrike().run();
};

const handleColorPicker = (color: string) => {
  currentColor.value = color;

  getFocus().setColor(color).run();
};

const handleToggleList = (type: LIST_TYPE) => {
  currentListType.value = type;

  if (type === LIST_TYPE.TASK) {
    getFocus().toggleTaskList().run();
  } else if (type === LIST_TYPE.ORDERED) {
    getFocus().toggleOrderedList().run();
  } else {
    getFocus().toggleBulletList().run();
  }
};

const handleToggleAlign = (alignment: ALIGN) => {
  currentAlign.value = alignment;

  getFocus().setTextAlign(alignment).run();
};

const handleAddIndent = () => {
  getFocus().increaseIndent().run();
};

const handleRemoveIndent = () => {
  getFocus().decreaseIndent().run();
};

const handleAddLink = (link: Link) => {
  getFocus()
    .extendMarkRange("link") // para que edite si ya hay uno
    .setLink({ href: link.url })
    .run();
};

const handleCleanLetter = () => {
  getFocus()
    .unsetBold()
    .unsetItalic()
    .unsetUnderline()
    .unsetStrike()
    .unsetColor()
    .unsetMark("textStyle") // Esto limpia fontSize, fontFamily, color si aún queda algo
    .unsetFontSize()
    .unsetFontFamily()
    .unsetLink()
    .run();
};

const handleChangesSelectionUpdate = () => {
  const attrs = getAttributes("textStyle");

  if (!hasValue(attrs)) return undefined;

  if (isValidString(attrs.fontFamily)) {
    const font = FONT_OPTIONS.find((o) => o.value.toLowerCase() === attrs.fontFamily.toLowerCase());

    currentFontFamily.value = font;
  } else {
    currentFontFamily.value = FONT_OPTIONS[0];
  }

  if (isValidString(attrs.fontSize)) {
    currentFontSize.value = parseInt(attrs.fontSize);
  } else {
    currentFontSize.value = 16;
  }

  if (isValidString(attrs.color)) {
    currentColor.value = attrs.color;
  } else {
    currentColor.value = BASE_COLOR;
  }

  if (isActive({ textAlign: ALIGN.LEFT })) {
    currentAlign.value = ALIGN.LEFT;
  } else if (isActive({ textAlign: ALIGN.CENTER })) {
    currentAlign.value = ALIGN.CENTER;
  } else if (isActive({ textAlign: ALIGN.RIGHT })) {
    currentAlign.value = ALIGN.RIGHT;
  } else if (isActive({ textAlign: ALIGN.JUSTIFY })) {
    currentAlign.value = ALIGN.JUSTIFY;
  } else {
    currentAlign.value = undefined;
  }

  if (isActive(LIST_TYPE.BULLET)) {
    currentListType.value = LIST_TYPE.BULLET;
  } else if (isActive(LIST_TYPE.ORDERED)) {
    currentListType.value = LIST_TYPE.ORDERED;
  } else if (isActive(LIST_TYPE.TASK)) {
    currentListType.value = LIST_TYPE.TASK;
  } else {
    currentListType.value = undefined;
  }

  if (isActive("link")) {
    currentLink.value = true;
  } else {
    currentLink.value = false;
  }
};

watch(
  () => props.zoom,
  (newValue, oldValue) => {
    if (oldValue === newValue) return;

    updateZoom(newValue);
  }
);

onBeforeUnmount(() => {
  getEditor().off("selectionUpdate", handleChangesSelectionUpdate);
});

onMounted(() => {
  updateZoom(props.zoom);
  handleChangesSelectionUpdate();

  getEditor().on("selectionUpdate", handleChangesSelectionUpdate);
});
</script>

<template>
  <div class="toolbar">
    <div class="toolbar__block">
      <TcIconButton
        class="toolbar__action_icon"
        iconName="Undo"
        size="sm"
        color="content-high"
        @click="() => getFocus().undo().run()"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="Redo"
        size="sm"
        color="content-high"
        @click="() => getFocus().redo().run()"
      />
    </div>

    <div class="toolbar__block">
      <TcSelect
        class="toolbar__select"
        name="select_zoom"
        fitContent
        isSmall
        :modelValue="currentZoom"
        :options="ZOOM_OPTIONS"
        :gutterBottom="false"
        @change="handleChangeZoom"
      />
    </div>

    <div class="toolbar__block">
      <TcSelect
        class="toolbar__select"
        name="select_typo_type"
        fitContent
        isSmall
        :modelValue="selectedTextTypeOption()"
        :options="textOptions"
        :gutterBottom="false"
        @change="handleSelectTypo"
      />

      <TcSelect
        class="toolbar__select"
        name="select_typo_font"
        fitContent
        isSmall
        :modelValue="currentFontFamily"
        :options="fontOptions"
        :gutterBottom="false"
        @change="handleSelectFont"
      />

      <NumberField :modelValue="currentFontSize" @update:modelValue="handleSetFontSize" />
    </div>

    <div class="toolbar__block">
      <TcIconButton
        class="toolbar__action_icon"
        iconName="Bold"
        size="sm"
        color="content-high"
        @click="handleBold"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="Italic"
        size="sm"
        color="content-high"
        @click="handleItalic"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="Underline"
        size="sm"
        color="content-high"
        @click="handleUnderline"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="Strike"
        size="sm"
        color="content-high"
        @click="handleStrike"
      />

      <ColorPicker v-model="currentColor" @change="handleColorPicker" />

      <LinkField
        class="toolbar__action_icon"
        :class="{ 'toolbar__action_icon--active': currentLink }"
        :editor="editor"
        @add="handleAddLink"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="Letter"
        size="sm"
        color="content-high"
        @click="handleCleanLetter"
      />
    </div>

    <div class="toolbar__block_separator hide-mobile" />

    <div class="toolbar__block">
      <TcIconButton
        class="toolbar__action_icon"
        iconName="List"
        size="sm"
        color="content-high"
        :class="{ 'toolbar__action_icon--active': currentListType === LIST_TYPE.BULLET }"
        @click="handleToggleList(LIST_TYPE.BULLET)"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="ListWithNumbers"
        size="sm"
        color="content-high"
        :class="{ 'toolbar__action_icon--active': currentListType === LIST_TYPE.ORDERED }"
        @click="handleToggleList(LIST_TYPE.ORDERED)"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="ListWithChecks"
        size="sm"
        color="content-high"
        :class="{ 'toolbar__action_icon--active': currentListType === LIST_TYPE.TASK }"
        @click="handleToggleList(LIST_TYPE.TASK)"
      />
    </div>

    <div class="toolbar__block">
      <TcIconButton
        class="toolbar__action_icon"
        iconName="TextLeft"
        size="sm"
        color="content-high"
        :class="{ 'toolbar__action_icon--active': currentAlign === ALIGN.LEFT }"
        @click="handleToggleAlign(ALIGN.LEFT)"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="TextCenter"
        size="sm"
        color="content-high"
        :class="{ 'toolbar__action_icon--active': currentAlign === ALIGN.CENTER }"
        @click="handleToggleAlign(ALIGN.CENTER)"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="TextRight"
        size="sm"
        color="content-high"
        :class="{ 'toolbar__action_icon--active': currentAlign === ALIGN.RIGHT }"
        @click="handleToggleAlign(ALIGN.RIGHT)"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="TextJustify"
        size="sm"
        color="content-high"
        :class="{ 'toolbar__action_icon--active': currentAlign === ALIGN.JUSTIFY }"
        @click="handleToggleAlign(ALIGN.JUSTIFY)"
      />
    </div>

    <div class="toolbar__block">
      <TcIconButton
        class="toolbar__action_icon"
        iconName="TextUnIndent"
        size="sm"
        color="content-high"
        @click="handleRemoveIndent()"
      />

      <TcIconButton
        class="toolbar__action_icon"
        iconName="TextAddIndent"
        size="sm"
        color="content-high"
        @click="handleAddIndent()"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">
.toolbar {
  display: flex;
  gap: $space-3;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  &__block {
    display: flex;
    gap: $space-2;
    align-items: center;
    justify-content: center;
    flex-wrap: nowrap;

    @media (max-width: $breakpoint-m) {
      flex-wrap: wrap;
    }
  }

  &__block_separator {
    @include colors.bg(content, low-solid);

    height: $space-6;
    width: 1px;
  }

  .toolbar__action_icon {
    @include radius(s);
    @include sp.padding(false, 1);

    display: flex;
    align-items: center;

    &:hover {
      @include colors.bg(content, low-solid);

      cursor: pointer;
    }

    &--active {
      @include colors.bg(secondary, mid-solid);
    }
  }
}
</style>
